import { useCallback, useEffect, useRef, useState, type CSSProperties, type RefObject } from "react";
import { fitStart, fitStep, type BridgeMessage, type FitState } from "@buildlab/preview";
import "./FitFrame.css";

// Fit the game to its area (Review 17, C61). The frame is drawn at a "design size" (the area divided
// by the scale) and shrunk with a CSS transform, so the student's whole page shows without
// scrolling; the bridge inside the frame reports the page's size (fit.ts has the rule). Clicks and
// keys still land where they should: the browser maps them through the transform.
// Play big shows the game full screen (Fullscreen API, or a full-window overlay if that's refused),
// where it may also scale up; Esc comes back. "100%" turns the fit off (the page scrolls instead).

const BIG_MAX = 4;

export interface Fit {
  stageRef: RefObject<HTMLDivElement>;
  frameStyle: CSSProperties;
  /** The scale the game shows at (1 when shown at 100%). */
  scale: number;
  /** True when the student picked "100%" (no fit; the page scrolls). */
  actual: boolean;
  setActual: (on: boolean) => void;
  big: boolean;
  setBig: (on: boolean) => void;
  stageClass: string;
}

/**
 * `frame` is the preview iframe; `buildKey` changes whenever a new frame is made (each build), so the
 * fit starts again from the current scale.
 */
export function useFit(frame: RefObject<HTMLIFrameElement>, buildKey: unknown): Fit {
  const stageRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  const boxRef = useRef(box);
  boxRef.current = box;
  const fit = useRef<FitState>(fitStart());
  const [scale, setScale] = useState(1);
  const [actual, setActual] = useState(false);
  const [big, setBigState] = useState(false);
  const bigRef = useRef(false);
  const viaFullscreen = useRef(false);

  // The area's size; a change starts the fit again (keeping the scale, so nothing jumps).
  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth, h = el.clientHeight;
      setBox((b) => (b && b.w === w && b.h === h ? b : { w, h }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    fit.current = fitStart(bigRef.current ? BIG_MAX : 1, fit.current.scroll ? 1 : fit.current.scale);
  }, [box, buildKey]);

  const exitBig = useCallback(() => {
    if (!bigRef.current) return;
    bigRef.current = false;
    setBigState(false);
    fit.current = fitStart(1, fit.current.scale);
    setScale(fit.current.scale);
    if (viaFullscreen.current && document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    viaFullscreen.current = false;
  }, []);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const w = frame.current?.contentWindow;
      if (!w || e.source !== w) return;
      const m = e.data as BridgeMessage;
      if (!m || m.__buildlab !== 1) return;
      if (m.kind === "escape") exitBig();
      // The area as it is now: right after a resize the frame can report before the ResizeObserver
      // above has updated `box`, and fitting to the old size left the game cut off (no later report came).
      const el = stageRef.current;
      const now = el && el.clientWidth > 0 && el.clientHeight > 0 ? { w: el.clientWidth, h: el.clientHeight } : boxRef.current;
      if (m.kind !== "size" || !now) return;
      const next = fitStep(fit.current, m, now);
      fit.current = next;
      setScale(next.scale);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [frame, exitBig]);

  // Leaving full screen with the browser's own Esc ends Play big too.
  useEffect(() => {
    const onChange = () => {
      if (!document.fullscreenElement && viaFullscreen.current) exitBig();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && bigRef.current) exitBig();
    };
    document.addEventListener("fullscreenchange", onChange);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      window.removeEventListener("keydown", onKey);
    };
  }, [exitBig]);
  useEffect(() => () => {
    if (viaFullscreen.current && document.fullscreenElement) void document.exitFullscreen().catch(() => {});
  }, []);

  const setBig = useCallback(
    (on: boolean) => {
      if (!on) return exitBig();
      if (bigRef.current) return;
      bigRef.current = true;
      setBigState(true);
      fit.current = fitStart(BIG_MAX, fit.current.scale);
      const el = stageRef.current;
      viaFullscreen.current = false;
      const focusGame = () => frame.current?.focus();
      if (el?.requestFullscreen) {
        el.requestFullscreen()
          .then(() => {
            viaFullscreen.current = true;
            focusGame();
          })
          .catch(focusGame); // refused (or not allowed here): the full-window overlay stands in
      } else focusGame();
    },
    [exitBig, frame],
  );

  const shown = actual ? 1 : scale;
  const frameStyle: CSSProperties =
    !box || shown === 1
      ? { width: "100%", height: "100%" }
      : { position: "absolute", left: 0, top: 0, width: box.w / shown, height: box.h / shown, transform: `scale(${shown})`, transformOrigin: "0 0" };
  return { stageRef, frameStyle, scale: shown, actual, setActual, big, setBig, stageClass: `fit-stage${big ? " fit-big" : ""}${actual ? " fit-actual" : ""}` };
}

const Expand = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="square" aria-hidden="true">
    <path d="M3 9V3h6M21 9V3h-6M3 15v6h6M21 15v6h-6" />
  </svg>
);

/** The scale (click for 100%) and the Play big button, for a pane header. */
export function FitControls({ fit, playBig = true }: { fit: Fit; playBig?: boolean }) {
  const pct = Math.round(fit.scale * 100);
  return (
    <>
      {(pct !== 100 || fit.actual) && (
        <button
          type="button"
          className="fit-scale"
          aria-pressed={fit.actual}
          title={fit.actual ? "Shown at full size: scroll to see all of it. Click to fit it in the space." : `Shrunk to ${pct}% so all of it fits. Click to see it at full size.`}
          onClick={() => fit.setActual(!fit.actual)}
        >
          {fit.actual ? "100%" : `${pct}%`}
        </button>
      )}
      {playBig && (
        <button type="button" className="ghost play-big" title="Play big: fill the screen with the game (Esc comes back)" onClick={() => fit.setBig(true)}>
          <Expand />
          <span className="bar-label">Play big</span>
        </button>
      )}
    </>
  );
}

/** Shown inside the stage while in Play big. */
export function BigExit({ fit }: { fit: Fit }) {
  if (!fit.big) return null;
  return (
    <button type="button" className="big-exit" onClick={() => fit.setBig(false)}>
      Back <kbd>Esc</kbd>
    </button>
  );
}
